<template>
  <section v-if="loading || data" class="dash-stats-section">
    <div class="dash-stats-section__head">
      <div>
        <h2 class="dash-stats-section__title">{{ $t("At a glance") }}</h2>
      </div>
      <button
        v-if="!loading && data"
        type="button"
        class="dash-stats-section__toggle"
        @click="toggleAllVisibility"
      >
        <feather-icon
          :icon="showAll ? 'EyeOffIcon' : 'EyeIcon'"
          size="16"
          class="mr-50"
        />
        {{ showAll ? $t("Hide values") : $t("Show all values") }}
      </button>
    </div>

    <div class="dash-stats-wrap">
      <div v-if="loading" class="dash-stats-grid dash-stats-skeleton">
        <div
          v-for="n in skeletonCount"
          :key="'stat-sk-' + n"
          class="dash-stat dash-stat--sk"
        >
          <b-skeleton width="55%" height="10px" animation="wave" class="mb-1" />
          <div class="d-flex align-items-center" style="gap: 0.5rem">
            <b-skeleton type="avatar" width="34px" height="34px" animation="wave" />
            <b-skeleton width="65%" height="18px" animation="wave" />
          </div>
          <b-skeleton width="45%" height="8px" animation="wave" class="mt-50" />
        </div>
      </div>

      <div v-else-if="data" class="dash-stats-grid">
        <statistics-card
          v-for="(card, index) in cards"
          :key="card.title + '-' + index"
          :title="card.title"
          :value="card.value"
          :icon="card.icon"
          :url="card.url"
          :other-text="card.otherText"
          :other-value="card.otherValue"
          :bottom-text="card.bottomText"
          :variant="card.variant"
          :is-visible="showAll"
          :card-index="index"
          @toggle-visibility="toggleAllVisibility"
        />
      </div>
    </div>
  </section>
</template>

<script>
import StatisticsCard from "./StatisticsCard.vue";
import { BSkeleton, VBTooltip } from "bootstrap-vue";

export default {
  components: {
    BSkeleton,
    StatisticsCard,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      required: false,
      default: null,
    },
    otherData: {
      type: Object,
      required: false,
      default: null,
    },
    visitorObj: {
      type: Object,
      required: false,
      default: null,
    },
    showCollectionMetrics: {
      type: Boolean,
      default: true,
    },
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      showAll: false,
      reportDomain: this.$store.state.report.replace(/_/g, ""),
      cards: [],
    };
  },
  computed: {
    skeletonCount() {
      if (this.reportDomain === "myskoolhappinessfoundation") return 3;
      if (!this.showCollectionMetrics) return 6;
      // Match the real grid (8 cards) so the placeholder → data swap does not
      // reflow the row and visibly "jump".
      return 8;
    },
  },
  watch: {
    // `dash` is a large payload; a deep watcher fired on every nested write and
    // rebuilt all cards several times per load (visible card flicker). A shallow
    // watcher on the replaced object is enough — the parent swaps the object.
    data: {
      immediate: true,
      handler(val) {
        if (val) this.buildCards();
      },
    },
    otherData: {
      handler() {
        if (this.data) this.buildCards();
      },
    },
    showCollectionMetrics() {
      if (this.data) this.buildCards();
    },
    loading(val) {
      if (!val && this.data) this.buildCards();
    },
    visitorObj() {
      if (this.data) this.buildCards();
    },
  },
  methods: {
    /** Users/Dashboard — optional until backend adds late-fee totals. */
    resolveTodayLateFeeAmount(data) {
      if (!data) return null;
      const keys = [
        "lateFeeCollection",
        "lateFeesCollection",
        "lateCollection",
        "todayLateFeeCollection",
        "collectionLateFee",
      ];
      for (let i = 0; i < keys.length; i += 1) {
        const v = data[keys[i]];
        if (v != null && v !== "") return v;
      }
      return null;
    },
    resolveTodayLateFeeCount(data) {
      if (!data) return null;
      const keys = [
        "lateFeeCollectionCount",
        "lateFeesCollectionCount",
        "lateCollectionCount",
        "todayLateFeeReceipts",
      ];
      for (let i = 0; i < keys.length; i += 1) {
        const v = data[keys[i]];
        if (v != null && v !== "") return v;
      }
      return null;
    },
    buildCards() {
      if (!this.data || !this.otherData) return;

      this.cards = [
        {
          title: "Today's Collection",
          value: (this.data.collection ?? 0).toLocaleString(),
          otherText: "",
          icon: "TrendingUpIcon",
          variant: "success",
          url: "",
          bottomText: "",
        },
      ];

      const lateFeeAmt = this.resolveTodayLateFeeAmount(this.data);
      const lateFeeCnt = this.resolveTodayLateFeeCount(this.data);
      this.cards.push({
        title: "Today's Late Fee Collection",
        value:
          lateFeeAmt != null
            ? Number(lateFeeAmt).toLocaleString()
            : "0",
        otherText: lateFeeCnt != null ? "Receipts" : "",
        otherValue: lateFeeCnt != null ? lateFeeCnt : "",
        icon: "ClockIcon",
        variant: "warning",
        url: "",
        bottomText: "",
      });

      this.cards.push(
        {
          title: "No. of Receipts",
          value: this.data.collectionCount ?? 0,
          otherText: "",
          icon: "FileTextIcon",
          variant: "info",
          url: "",
          bottomText: "",
        },
        {
          title: "Students",
          value: this.data.students ?? 0,
          otherText: "",
          icon: "UserIcon",
          variant: "primary",
          url: "",
          bottomText: "",
        },
        {
          title: "Staff",
          value: this.data.staff ?? 0,
          otherText: "",
          icon: "UserCheckIcon",
          variant: "warning",
          url: "",
          bottomText: "",
        },
        {
          title: "Inquiries",
          value: this.data.inquiries ?? 0,
          otherText: "",
          icon: "HelpCircleIcon",
          variant: "info",
          url: "",
          bottomText: "",
        },
        {
          title: "Dues",
          value: (this.data.dues ?? 0).toLocaleString(),
          otherText: "Students",
          otherValue: this.data.duesCount ?? 0,
          icon: "AlertCircleIcon",
          variant: "danger",
          url: "",
          bottomText: "",
        },
        {
          title: "Families",
          value: this.otherData.familyCount ?? 0,
          otherText: "",
          icon: "UsersIcon",
          variant: "primary",
          url: "",
          bottomText: "",
        },
        {
          title: "Monthly Estimation",
          value: (this.otherData.monthly ?? 0).toLocaleString(),
          otherText: "",
          icon: "ActivityIcon",
          variant: "success",
          url: "",
          bottomText: "",
        }
      );

      if (this.reportDomain === "myskoolhappinessfoundation") {
        const filteredCards = ["Students", "Staff"];
        this.cards = this.cards.filter((c) => filteredCards.includes(c.title));
        this.cards.push({
          title: "Donors",
          value: this.data.donors || 0,
          otherText: "",
          icon: "UsersIcon",
          variant: "danger",
          url: "",
          bottomText: "",
        });
      }

      if (
        this.reportDomain === "myskoolhpgs" &&
        this.$store.state.userData.cId == 1
      ) {
        const studentsCard = this.cards.find((c) => c.title === "Students");
        const staffCard = this.cards.find((c) => c.title === "Staff");
        if (studentsCard) {
          studentsCard.bottomText = `Ex-Students: ${
            this.data.ex_students ?? 0
          } | Total: ${
            (this.data.students ?? 0) + (this.data.ex_students ?? 0)
          }`;
        }
        if (staffCard) {
          staffCard.bottomText = `Academus: ${
            this.data.academus_staff ?? 0
          } | Total: ${(this.data.staff ?? 0) + (this.data.academus_staff ?? 0)}`;
        }
      }

      if (!this.showCollectionMetrics) {
        const collectionTitles = [
          "Today's Collection",
          "Today's Late Fee Collection",
          "No. of Receipts",
        ];
        this.cards = this.cards.filter(
          (c) => !collectionTitles.includes(c.title)
        );
      }

      this.setVisitor();
      this.setReport();
    },
    setVisitor() {
      if (!this.visitorObj) return;
      const exists = this.cards.some((c) => c.title === "Visitors");
      if (exists) return;
      const duesIndex = this.cards.findIndex((c) => c.title === "Dues");
      const insertAt = duesIndex >= 0 ? duesIndex : this.cards.length;
      this.cards.splice(insertAt, 0, {
        title: "Visitors",
        value: this.visitorObj.total,
        otherText: `${this.visitorObj.inCount} in, ${this.visitorObj.outCount} out`,
        icon: "InfoIcon",
        variant: "warning",
        url: "",
      });
    },
    toggleAllVisibility() {
      this.showAll = !this.showAll;
    },
    setReport() {
      let date = new Date().toJSON().split("T")[0];

      let url =
        `https://${this.reportDomain}.myskool.app/Fee/Show?type=dcr&dtFrom=` +
        date +
        "&dtTo=" +
        date +
        "&dbb=" +
        this.$store.state.userData.db +
        "&cID=" +
        this.$store.state.userData.cId +
        "&format=Class wise";

      const collectionCard = this.cards.find(
        (c) => c.title === "Today's Collection"
      );
      if (collectionCard) collectionCard.url = url;

      const receiptsCard = this.cards.find(
        (c) => c.title === "No. of Receipts"
      );
      if (receiptsCard) receiptsCard.url = url;
    },
  },
};
</script>

<style scoped lang="scss">
.dash-stats-section {
  margin-bottom: 1.25rem;
}

.dash-stats-section__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem 1rem;
  margin-bottom: 0.75rem;
  padding: 0;
}

.dash-stats-section__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: #2f2b3d;
}

.dash-stats-section__sub {
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
  max-width: 28rem;
  line-height: 1.45;
}

.dash-stats-section__toggle {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.7rem;
  border-radius: 8px;
  border: 1px solid #ebe9f1;
  background: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6e6b7b;
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease;
  box-shadow: none;

  &:hover {
    border-color: rgba(115, 103, 240, 0.35);
    color: #7367f0;
  }
}

.dash-stats-wrap {
  margin: 0;
  padding: 0.35rem 0.15rem 0.15rem;
}

.dash-stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.85rem 0.95rem;
  align-items: stretch;
}

@media (max-width: 1399px) {
  .dash-stats-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 991px) {
  .dash-stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.6rem;
  }
}

@media (max-width: 575px) {
  .dash-stats-grid {
    grid-template-columns: 1fr;
  }

  .dash-stats-section__head {
    align-items: flex-start;
  }

  .dash-stats-section__toggle {
    width: 100%;
    justify-content: center;
  }
}

.dash-stat--sk {
  min-height: 7.35rem;
  padding: 0.72rem 0.9rem;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.75);
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(12px);
  box-shadow: 0 8px 24px rgba(115, 103, 240, 0.06);
  pointer-events: none;
}
</style>
